<template>
    <div class="videocomponent" :class="{'portrait': portrait}" v-if="!isAndroidMode()">
        <video ref="video" :class="{'portrait': portrait}" autoplay></video>
        <div ref="detectionFrame" class="detection-frame" :class="{'portrait': portrait}"></div> <!-- Moldura branca -->
        <canvas ref="canvasRec"></canvas>
    </div>
</template>

<script>
import * as faceapi from 'face-api.js'
import eventModule from '@/assets/libs/eventModule'
import CameraController from '@/assets/controllers/CameraController'
import Util from '@/assets/libs/util'

export default {
    data () {
        return {
            isVideo: false,
            ctx: null,
            ctxRec: null,
            videoStream: null,
            coordinates: [],
            isProcessingRequest: false,
            lastDetectionTime: null,
            detectionThreshold: 1000, // Reduzido de 2000ms para 1000ms
            cooldown: false,
            isMisaligned: false,
            facesDetected: 0,
            pauseDetection: false,
            lastProcessTime: 0, // Controle de intervalo entre processamentos
            processInterval: 100 // Processa a cada 100ms ao invés de todo frame
        }
    },
    props: ['portrait', 'camTest'],
    watch: {
        pauseDetection () {
            this.clearRect()
        }
    },
    methods: {
        async loadModels () {
            await faceapi.nets.tinyFaceDetector.loadFromUri('./static/models')
            await faceapi.nets.faceRecognitionNet.loadFromUri('./static/models')
        },
        async startVideo () {
            // Obtém o deviceId da câmera configurada para a tela "face"
            const faceCameraId = await CameraController.getCameraDeviceForScreen('face')
            if (navigator.mediaDevices && faceCameraId) {
                try {
                    // Usa o deviceId para selecionar a câmera do "face"
                    const stream = await navigator.mediaDevices.getUserMedia({
                        video: this.getVideoParams(faceCameraId)
                    })

                    this.isVideo = true
                    const video = this.$refs.video

                    if (video) {
                        video.srcObject = stream
                        this.videoStream = stream

                        video.onloadedmetadata = () => {
                            this.setCanvasSize(video)
                            video.play()
                            this.detectFaces()
                        }
                    }
                } catch (err) {
                    console.error('Erro ao acessar a câmera configurada para face:', err)
                }
            } else {
                console.error('Dispositivo de câmera para face não configurado ou indisponível')
            }
        },
        setCanvasSize (video) {
            const canvasRec = this.$refs.canvasRec
            const { videoWidth, videoHeight } = video
            canvasRec.width = videoWidth
            canvasRec.height = videoHeight
            this.ctxRec = canvasRec.getContext('2d')
        },
        stopVideo () {
            if (this.videoStream) {
                this.videoStream.getTracks().forEach(track => track.stop())
            }
            this.isVideo = false
            const video = this.$refs.video
            if (video) {
                video.srcObject = null
            }
        },
        async detectFaces () {
            const video = this.$refs.video
            const canvasRec = this.$refs.canvasRec
            // Otimizado: inputSize menor (160) e scoreThreshold mais alto (0.5) para detecção mais rápida
            const options = new faceapi.TinyFaceDetectorOptions({
                inputSize: 160, // Tamanho menor = processamento mais rápido (128, 160, 224, 320, 416, 512, 608)
                scoreThreshold: 0.5 // Threshold mais alto = detecções mais confiantes e rápidas
            })

            const detect = async () => {
                if (!this.isVideo) {
                    return
                }

                const now = Date.now()

                if (!this.pauseDetection) {
                    // Só processa se passou o intervalo mínimo desde o último processamento
                    if (now - this.lastProcessTime >= this.processInterval) {
                        this.lastProcessTime = now

                        let detections = await faceapi.detectAllFaces(video, options)
                        if (detections) {
                            detections = Array.isArray(detections) ? detections : [detections]
                            this.updateFaceState(detections)
                            this.coordinates = detections.map(det => det && det.box)
                            this.ctxRec.clearRect(0, 0, canvasRec.width, canvasRec.height)
                            this.drawRectangle()
                            if (!this.isMisaligned && this.facesDetected === 1 && !this.camTest) {
                                this.handleFaceDetection()
                            } else {
                                this.lastDetectionTime = null
                            }
                        } else {
                            this.clearRect()
                        }
                    }
                } else {
                    this.clearRect()
                }

                requestAnimationFrame(detect)
            }

            detect()
        },
        clearRect () {
            const canvasRec = this.$refs.canvasRec
            this.ctxRec.clearRect(0, 0, canvasRec.width, canvasRec.height)
        },
        updateFaceState (detections) {
            this.facesDetected = detections.length
            this.isMisaligned =
                this.facesDetected > 1 ||
                detections.some(det => det && det.score < 0.5) // Ajustado de 0.6 para 0.5 para consistência
        },
        handleFaceDetection () {
            const now = Date.now()

            if (!this.lastDetectionTime) {
                this.lastDetectionTime = now
                return
            }

            if (now - this.lastDetectionTime >= this.detectionThreshold &&
                !this.cooldown) {
                this.initiateFaceRecognition()
            }
        },
        async initiateFaceRecognition () {
            // await this.sendFaceRecognitionRequest()

            const imageBase64 = this.getImageBase64()
            eventModule.$emit('faceDetected', {
                imageBase64: imageBase64
            })

            this.cooldown = true
            setTimeout(() => {
                this.cooldown = false
            }, 1000) // Reduzido de 2000ms para 1000ms para permitir nova detecção mais rápida
        },
        drawRectangle () {
            const frame = this.$refs.detectionFrame // Referência à moldura

            if (frame) {
                if (this.facesDetected > 0) {
                    frame.style.opacity = 0.8 // Exibe a moldura com transição suave
                    for (let i = 0; i < this.coordinates.length; i++) {
                        const { x, y, width, height } = this.coordinates[i]
                        this.ctxRec.strokeStyle = this.isMisaligned ? 'red' : 'green'
                        this.ctxRec.lineWidth = 2
                        this.ctxRec.strokeRect(x, y - 30, width, height)
                    }
                } else {
                    frame.style.opacity = 0 // Oculta a moldura com transição suave
                }
            }
        },
        getImageBase64 () {
            const video = this.$refs.video
            const canvas = document.createElement('canvas') // Cria um canvas temporário

            // Define as dimensões do canvas com base nas dimensões do vídeo
            canvas.width = video.videoWidth
            canvas.height = video.videoHeight
            const ctx = canvas.getContext('2d')

            // Desenha o frame atual do vídeo no canvas
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

            // Retorna a imagem em base64 (formato JPEG)
            return canvas.toDataURL('image/jpeg').replace('data:image/jpeg;base64,', '')
        },
        setPauseDetection (value) {
            this.clearRect()
            this.pauseDetection = value
        },
        async restartVideoComponent () {
            this.stopVideo()
            await this.loadModels()
            await this.startVideo()
        },
        getVideoParams (faceCameraId) {
            const isGertecMode = Util.isGertecSk210()
            let videoWidth = 0
            let videoHeight = 0
            if (isGertecMode && this.portrait) {
                videoWidth = 200
                videoHeight = 250
            } else if (this.portrait) {
                // Reduzido de 900x1600 para 640x1136 - suficiente para detecção facial e mais rápido
                videoWidth = 550
                videoHeight = 730
            } else {
                // Reduzido de 1600x900 para 1280x720 (720p) - suficiente para detecção facial
                videoWidth = 1280
                videoHeight = 720
            }

            return {
                deviceId: { exact: faceCameraId }, // Especifica a câmera do "face"
                width: videoWidth,
                height: videoHeight,
                frameRate: { ideal: 15, max: 20 } // Reduzido de 24 para 15 fps - suficiente para detecção
            }
        },
        isAndroidMode: function () {
            return Util.isAndroidMode()
        }

    },
    async mounted () {
        await this.loadModels()
        if (!this.isAndroidMode()) {
            this.startVideo()
        }
    },
    created () {
        eventModule.$on('update:pauseDetection', this.setPauseDetection)
        eventModule.$on('restartVideoComponent', this.restartVideoComponent)
        eventModule.$on('stopVideo', this.stopVideo)
    },
    beforeRouteLeave (to, from, next) {
        this.stopVideo()
        next()
    },
    beforeDestroy () {
        this.stopVideo()
        eventModule.$off('update:pauseDetection')
        eventModule.$off('restartVideoComponent')
        eventModule.$off('stopVideo')
    }
}

</script>

<style scoped lang='less'>
.videocomponent {
    background-color: white;
    position: relative;
    display: inline-block;
    width: 100%;
    height: 100%;
    video,
    canvas {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: contain;
        &.portrait {
            text-align: center;
            object-fit: cover;
            width: 550px;
            height: 730px;
            margin: auto;
            background-color: rgb(175, 175, 175);
            border-radius: 10px;
        }
    }

    canvas {
        position: absolute;
        top: 0;
        left: 0;
        pointer-events: none;
    }

    .detection-frame {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%; /* Ajuste para a borda interna */
        height: 100%; /* Ajuste para a borda interna */
        border: 50px solid white; /* Aumenta a espessura da borda */
        border-left: 250px solid white;
        border-right: 250px solid white;
        border-radius: 0px; /* Arredonda todos os cantos */
        z-index: 0; /* Garante que o frame esteja acima do vídeo e canvas */
        opacity: 0; /* Início com opacidade zero */
        transition: opacity 0.5s ease; /* Transição suave de opacidade */
        pointer-events: none;
        box-sizing: border-box; /* Garante que o arredondamento seja na borda */
        &.portrait {
            margin: auto;
            left: 0;
            right: 0;
            width: 550px;
            height: 730px;
            // transform: translateX(43%);
            border: 50px solid white;
            border-radius: 10px;
        }
    }
}

@media (max-width: 698px) { // Gertec SK210
    .videocomponent {
        position: relative;
        display: inline-block;
        width: -webkit-fill-available;
        // width: 100%;
        // height: 100%;
        video,
        canvas {
            display: block;
            // width: 100%;
            width: -webkit-fill-available !important;
            height: 100%;
            object-fit: contain;
            &.portrait {
                text-align: center;
                object-fit: cover;
                width: 200px;
                height: 250px;
                margin: auto;
                background-color: rgb(175, 175, 175);
                border-radius: 10px;
            }
        }

        canvas {
            position: absolute;
            top: 0;
            left: 0;
            pointer-events: none;
        }

        .detection-frame {
            position: absolute;
            // top: 0;
            // left: 0;
            width: 100%; /* Ajuste para a borda interna */
            height: 100%; /* Ajuste para a borda interna */
            border: 10px solid white; /* Aumenta a espessura da borda */
            border-radius: 0px; /* Arredonda todos os cantos */
            z-index: 0; /* Garante que o frame esteja acima do vídeo e canvas */
            opacity: 0; /* Início com opacidade zero */
            transition: opacity 0.5s ease; /* Transição suave de opacidade */
            pointer-events: none;
            box-sizing: border-box; /* Garante que o arredondamento seja na borda */
            &.portrait {
                // left: 0;
                // right: 0;
                width: -webkit-fill-available !important;
                transform: translateX(0);
                border: 10px solid white;
                border-radius: 10px;
            }
        }
        &.portrait {
            width: 200px;
            height: 250px;
        }
    }
}

@media (min-width: 699px) and (max-width: 1025px) { //@tablet
    .videocomponent {
        background-color: white;
        position: relative;
        display: inline-block;
        width: 100%;
        height: 100%;
        video,
        canvas {
            display: block;
            width: 100%;
            height: 100%;
            object-fit: contain;
            &.portrait {
                text-align: center;
                object-fit: cover;
                width: 400px;
                height: 550px;
                margin: auto;
                background-color: rgb(175, 175, 175);
                border-radius: 10px;
            }
        }

        canvas {
            position: absolute;
            top: 0;
            left: 0;
            pointer-events: none;
        }

        .detection-frame {
            position: absolute;
            // top: 0;
            // left: 0;
            width: 100%; /* Ajuste para a borda interna */
            height: 100%; /* Ajuste para a borda interna */
            border: 10px solid white; /* Aumenta a espessura da borda */
            border-left: 120px solid white;
            border-right: 120px solid white;
            border-radius: 0px; /* Arredonda todos os cantos */
            z-index: 0; /* Garante que o frame esteja acima do vídeo e canvas */
            opacity: 0; /* Início com opacidade zero */
            transition: opacity 0.5s ease; /* Transição suave de opacidade */
            pointer-events: none;
            box-sizing: border-box; /* Garante que o arredondamento seja na borda */
            &.portrait {
                margin: auto;
                left: 0;
                right: 0;
                width: 400px;
                height: 550px;
                // transform: translateX(84px);
                border: 50px solid white;
                border-radius: 10px;
            }
        }
    }
}

@media (orientation: landscape) {
    .videocomponent {
        video,
        canvas {
            display: block;
            width: 100%;
            height: 100%;
            object-fit: contain;
            &.portrait {
                text-align: center;
                object-fit: cover;
                width: 400px;
                height: 350px;
                margin: auto;
                background-color: rgb(175, 175, 175);
                border-radius: 10px;
            }
        }

        canvas {
            position: absolute;
            top: 0;
            left: 0;
            pointer-events: none;
        }

        .detection-frame {
            position: absolute;
            // top: 0;
            // left: 0;
            width: 100%; /* Ajuste para a borda interna */
            height: 100%; /* Ajuste para a borda interna */
            border: 10px solid white; /* Aumenta a espessura da borda */
            border-left: 180px solid white;
            border-right: 180px solid white;
            border-radius: 0px; /* Arredonda todos os cantos */
            z-index: 0; /* Garante que o frame esteja acima do vídeo e canvas */
            opacity: 0; /* Início com opacidade zero */
            transition: opacity 0.5s ease; /* Transição suave de opacidade */
            pointer-events: none;
            box-sizing: border-box; /* Garante que o arredondamento seja na borda */
            &.portrait {
                margin: auto;
                left: 0;
                right: 0;
                width: 400px;
                height: 350px;
                // transform: translateX(84px);
                border: 30px solid white;
                border-radius: 10px;
            }
        }
    }
}
</style>



// WEBPACK FOOTER //
// src/components/AIFaceVideoComponent.vue